import { getCoordinatesOfTimeZone } from "./timeZoneCoordinates";

// When it is day, twilight and night where the person is. This is what the default appearance follows
// (the owner, 2026-10-02): light by day, dark at night, and for an hour in between the tree and river
// scenes are painted at dusk, to lead from one into the other.
//
// The sun's times come from the date and from where the device's time zone is (timeZoneCoordinates.ts):
// a browser tells every page its time zone, so nobody is asked for their location. A zone covers a wide
// area, so the times can be off by half an hour or so for someone far from the zone's city; the twilight
// hour absorbs that.
//
//   day ──▶ twilight (light page, dusk scenes) ──▶ night (dark page) ──▶ twilight ──▶ day
//           starts half an hour before sunset      starts half an hour     ends half an hour
//                                                   after sunset            after sunrise

export type DayPhase = "day" | "twilight" | "night";

/** Minutes after local midnight. Night runs from `nightStartsAt` through midnight to `nightEndsAt`. */
export type DayNightSchedule = {
  nightStartsAt: number;
  nightEndsAt: number;
};

export const TWILIGHT_MINUTES = 60;
// The page turns dark when the light has gone, about half an hour after the sun sets, and light again
// about half an hour before it rises.
const MINUTES_FROM_SUNSET_TO_NIGHT = 30;

// "Night" is also a time of day. However the sun behaves (a northern summer, where it sets at eleven or
// not at all; a northern winter, where it sets at three), the page turns dark no earlier than five in the
// afternoon and no later than half past nine, and light again between five and eight in the morning.
const EARLIEST_NIGHT_START = 17 * 60;
const LATEST_NIGHT_START = 21 * 60 + 30;
const EARLIEST_NIGHT_END = 5 * 60;
const LATEST_NIGHT_END = 8 * 60;

// A device whose time zone is not a place (UTC), or a browser that does not say: night from seven in the
// evening to six in the morning. index.html uses the same two numbers before it has a stored schedule.
export const FALLBACK_DAY_NIGHT_SCHEDULE: DayNightSchedule = { nightStartsAt: 19 * 60, nightEndsAt: 6 * 60 };

const MINUTES_PER_DAY = 24 * 60;
const DEGREES_TO_RADIANS = Math.PI / 180;
// The sun counts as set when its center is this far below the horizon (its disc and the bending of
// light in the air are the 0.833 degrees)
const SUN_ANGLE_AT_SUNSET = 90.833 * DEGREES_TO_RADIANS;

function clamp(value: number, lowest: number, highest: number): number {
  return Math.min(Math.max(value, lowest), highest);
}

function getDayOfYear(date: Date): number {
  const startOfYear = new Date(date.getFullYear(), 0, 0);
  return Math.floor((date.getTime() - startOfYear.getTime()) / 86_400_000);
}

export function getDeviceTimeZoneName(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/**
 * Local sunrise and sunset on the day of `date`, in minutes after local midnight, by NOAA's short solar
 * formulas (good to a minute or two, far finer than a time zone's width). `neverSets` and `neverRises`
 * are the polar summer and winter.
 */
export function getSunTimes(
  date: Date,
  coordinates: { latitude: number; longitude: number }
): { sunrise: number; sunset: number } | { neverSets: true } | { neverRises: true } {
  const yearAngle = ((2 * Math.PI) / 365) * (getDayOfYear(date) - 1);
  // How far the sundial runs ahead of the clock, in minutes
  const equationOfTime =
    229.18 *
    (0.000075 +
      0.001868 * Math.cos(yearAngle) -
      0.032077 * Math.sin(yearAngle) -
      0.014615 * Math.cos(2 * yearAngle) -
      0.040849 * Math.sin(2 * yearAngle));
  const sunDeclination =
    0.006918 -
    0.399912 * Math.cos(yearAngle) +
    0.070257 * Math.sin(yearAngle) -
    0.006758 * Math.cos(2 * yearAngle) +
    0.000907 * Math.sin(2 * yearAngle) -
    0.002697 * Math.cos(3 * yearAngle) +
    0.00148 * Math.sin(3 * yearAngle);

  const latitude = coordinates.latitude * DEGREES_TO_RADIANS;
  const cosineOfHalfDay =
    Math.cos(SUN_ANGLE_AT_SUNSET) / (Math.cos(latitude) * Math.cos(sunDeclination)) -
    Math.tan(latitude) * Math.tan(sunDeclination);
  if (cosineOfHalfDay < -1) return { neverSets: true };
  if (cosineOfHalfDay > 1) return { neverRises: true };
  // The sun moves a degree in four minutes
  const halfDayMinutes = (Math.acos(cosineOfHalfDay) / DEGREES_TO_RADIANS) * 4;

  const minutesAheadOfUtc = -date.getTimezoneOffset();
  const solarNoonUtc = 720 - 4 * coordinates.longitude - equationOfTime;
  const solarNoon = (((solarNoonUtc + minutesAheadOfUtc) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  return { sunrise: solarNoon - halfDayMinutes, sunset: solarNoon + halfDayMinutes };
}

/** When the page is dark on the day of `date` for a device in `timeZoneName`. */
export function getDayNightSchedule(
  date: Date,
  timeZoneName: string | null = getDeviceTimeZoneName()
): DayNightSchedule {
  const coordinates = timeZoneName ? getCoordinatesOfTimeZone(timeZoneName) : null;
  if (!coordinates) return FALLBACK_DAY_NIGHT_SCHEDULE;

  const sunTimes = getSunTimes(date, coordinates);
  if ("neverSets" in sunTimes) return { nightStartsAt: LATEST_NIGHT_START, nightEndsAt: EARLIEST_NIGHT_END };
  if ("neverRises" in sunTimes) return { nightStartsAt: EARLIEST_NIGHT_START, nightEndsAt: LATEST_NIGHT_END };
  return {
    nightStartsAt: Math.round(
      clamp(sunTimes.sunset + MINUTES_FROM_SUNSET_TO_NIGHT, EARLIEST_NIGHT_START, LATEST_NIGHT_START)
    ),
    nightEndsAt: Math.round(
      clamp(sunTimes.sunrise - MINUTES_FROM_SUNSET_TO_NIGHT, EARLIEST_NIGHT_END, LATEST_NIGHT_END)
    ),
  };
}

export function getDayPhaseAt(date: Date, schedule: DayNightSchedule): DayPhase {
  const minutesAfterMidnight = date.getHours() * 60 + date.getMinutes();
  if (minutesAfterMidnight >= schedule.nightStartsAt || minutesAfterMidnight < schedule.nightEndsAt) return "night";
  const isMorningTwilight = minutesAfterMidnight < schedule.nightEndsAt + TWILIGHT_MINUTES;
  const isEveningTwilight = minutesAfterMidnight >= schedule.nightStartsAt - TWILIGHT_MINUTES;
  return isMorningTwilight || isEveningTwilight ? "twilight" : "day";
}

// index.html paints the first frame before the app has loaded, so it cannot work out the sun's times.
// It reads the schedule the app stored on an earlier visit ("<nightStartsAt>,<nightEndsAt>"); the times
// move by a minute or two a day, so yesterday's are right enough for one frame.
export const DAY_NIGHT_SCHEDULE_STORAGE_KEY = "jungle-day-night-schedule";
let lastStoredSchedule: string | null = null;

function storeScheduleForTheNextFirstPaint(schedule: DayNightSchedule): void {
  const scheduleToStore = `${schedule.nightStartsAt},${schedule.nightEndsAt}`;
  if (scheduleToStore === lastStoredSchedule) return;
  lastStoredSchedule = scheduleToStore;
  try {
    window.localStorage.setItem(DAY_NIGHT_SCHEDULE_STORAGE_KEY, scheduleToStore);
  } catch {
    // Storage can be unavailable (private windows): the next first frame uses the fallback hours.
  }
}

/** Day, twilight or night right now on this device; also leaves today's schedule for the next first paint. */
export function getCurrentDayPhase(now: Date = new Date()): DayPhase {
  const schedule = getDayNightSchedule(now);
  storeScheduleForTheNextFirstPaint(schedule);
  return getDayPhaseAt(now, schedule);
}
